import React, { useState, useEffect, useRef } from "react";
import { Spinner, Form, Button } from "react-bootstrap";
import SearchFilter from '../searchFilter';
import FarmSubscriptions from './FarmSubscriptions';
import DoneModal from '../../group/InvitationModal/inviteDone';
import farmService from '../../farm/duck/farmService';
import groupService from '../../group/duck/groupService';
import StandardEmpty from '../../common/standardEmptyCase';
import siteService from '../../sites/duck/siteService';
import subscriptionService from '../subscriptions/duck/subscriptionService';


const NO_DATA = false; // Set to true to skip API calls and render empty page

function renderCollection(data, mapping, emptyCaseMessage) {
  if (!data) {
    return <Spinner animation="grow" size="lg" />
  } else if (data.length === 0) {
    return emptyCaseMessage && <span>{emptyCaseMessage}</span>;
  } else if (data.length > 0) {
    return data.map(mapping)
  }
}

function SubscriptionHistory(props) {

  const [farmsData, setFarmsData] = useState(null)
  const [allFarmsData, setAllFarmsData] = useState(null) // Store all farms for filtering
  const [search, setSearch] = useState('');
  const [farmSearch, setFarmSearch] = useState('');
  const previousSearch = useRef(null);
  const previousFarmSearch = useRef(null);
  const [parent, setParent] = useState(null);
  const [doneModalShow, setDoneModalShow] = useState(false);
  const [doneSubscrptionModal, setDoneSubscrptionModal] = useState(false);
  const [silverSites, setSilverSites] = useState("0");
  const [GoldSites, setGoldSites] = useState("0");
  const [notSubscribedSites, setNotSubscribedSites] = useState("0");
  const [allSites, setAllSites] = useState(null);
  const [activeNewSubscription, setActiveNewSubscriptions] = useState([])
  const [sitesByFarm, setSitesByFarm] = useState(null); // NEW: Map of farmId -> sites[]

  const [isEmptyResult, setIsEmptyResult] = useState(null);

  // Filter states
  const [planFilters, setPlanFilters] = useState({
    Gold: true,
    Silver: true,
    None: true
  });
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'active', 'inactive'

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [originalPlans, setOriginalPlans] = useState({}); // Store original plans: { siteId: plan }
  const [isSaving, setIsSaving] = useState(false); // Track save operation

  useEffect(() => {
    if (NO_DATA) {
      setFarmsData([]);
      setParent(null);
      setAllSites([]);
      setSitesByFarm({});
      setOriginalPlans({});
      return;
    }

    const reqObj = farmService.getFarmsForUserRequest();
    reqObj.request()
      .then(farms => {
        const farmsList = farms.map(({ objectId, name }) => ({ objectId, name }));
        setAllFarmsData(farmsList); // Store all farms
        setFarmsData(farmsList);    // Initially show all farms
      })
      .catch(error => console.log(error));
    return () => reqObj.cancel('Farms cancelled');
  }, []);

  useEffect(() => {
    if (NO_DATA) return;

    groupService.getParent()
      .then(data => setParent(data))
      .catch(err => console.log(err));
  }, []);

  useEffect(() => {
    if (NO_DATA) return;

    // OPTIMIZATION: Single API call to fetch ALL sites across all farms
    // This replaces multiple per-farm API calls that were causing performance issues
    let totalSites = []
    subscriptionService.getAllSiteDetails()
      .then(data => {
        // eslint-disable-next-line no-unused-vars
        let allSites1 = data?.map(site1 =>
          site1.sites.map(site => totalSites.push({ ...site, dealerName: site1.dealerName }))
        )
        setAllSites(totalSites);

        // Group sites by farmId so each FarmSubscriptions component can use preloaded data
        // instead of making individual API calls
        // Also normalize field names to match what FarmSubscriptions expects
        const farmMap = {};
        totalSites.forEach(site => {
          const farmId = site.farmId;
          if (!farmMap[farmId]) {
            farmMap[farmId] = [];
          }
          // Normalize data structure to match getSiteDetailsWithSubscriptions format
          const normalizedSite = {
            ...site,
            name: site.siteName || site.name, // siteName from getAllSiteDetails -> name
            planDetails: site.planDetails || { plan: site.plan } // Ensure planDetails exists
          };
          farmMap[farmId].push(normalizedSite);
        });
        setSitesByFarm(farmMap);

        // Store original plans for all sites
        const plans = {};
        totalSites.forEach(site => {
          plans[site.siteId] = site.plan || 'None';
        });
        setOriginalPlans(plans);

        const silverSites1 = totalSites && totalSites.filter(site => (
          site.plan === "Silver"
        ))
        const goldSites1 = totalSites && totalSites.filter(site => (
          site.plan === "Gold"
        ))
        const noneSites1 = totalSites && totalSites.filter(site => (
          site.plan === "None"
        ))
        setSilverSites(silverSites1?.length);
        setGoldSites(goldSites1?.length);
        setNotSubscribedSites(noneSites1?.length);
      }
      )
      .catch(err => console.log(err));
  }, []);

  function handleUpdateSubscriptionsClick() {
    setIsEditMode(true);
  }

  function handleCancelEdit() {
    // Reset all changes
    setActiveNewSubscriptions([]);
    setIsEditMode(false);
  }

  function handleSaveSubscriptions() {
    const removeSameSubscribedPlan = activeNewSubscription.filter(data => data.oldPlan !== data.currentPlan)

    if (removeSameSubscribedPlan.length === 0) {
      // No actual changes to save
      setActiveNewSubscriptions([]);
      setIsEditMode(false);
      return;
    }

    console.log('Saving subscription changes:', removeSameSubscribedPlan);

    setIsSaving(true);

    siteService.activateSubscriptionsOrComments(removeSameSubscribedPlan)
      .then(() => {
        setActiveNewSubscriptions([]);
        setIsEditMode(false);
        setIsSaving(false);
        setDoneSubscrptionModal(true);
      })
      .catch(error => {
        console.error('Error saving subscriptions:', error);
        setIsSaving(false);
        // Keep edit mode open on error so user can retry
      });
  }

  function applyFilters(sites, searchQuery = search) {
    if (!sites) return null;

    return sites.filter(site => {
      // Text search filter
      const matchesSearch = !searchQuery ||
        site?.siteId?.startsWith(searchQuery) ||
        site?.siteName?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
        site?.dealerName?.toLowerCase().includes(searchQuery?.toLowerCase());

      // Plan filter
      const sitePlan = site.plan || 'None';
      const matchesPlan = planFilters[sitePlan];

      // Active filter
      const isActive = site.status === 'ACTIVE';
      const matchesActive = activeFilter === 'all' ||
        (activeFilter === 'active' && isActive) ||
        (activeFilter === 'inactive' && !isActive);

      return matchesSearch && matchesPlan && matchesActive;
    });
  }

  function updateSummary(filteredSites) {
    const silverSites2 = filteredSites && filteredSites.filter(site => site.plan === "Silver");
    const goldSites2 = filteredSites && filteredSites.filter(site => site.plan === "Gold");
    const noneSites2 = filteredSites && filteredSites.filter(site => (site.plan === "None" || !site.plan));

    setSilverSites(silverSites2?.length);
    setGoldSites(goldSites2?.length);
    setNotSubscribedSites(noneSites2?.length);
  }

  function handleSiteSearch(event) {
    let trimmedstr = event.target.value;
    const query = trimmedstr.replace(/\s+$/, '');

    if (previousSearch.current !== null) {
      clearTimeout(previousSearch.current);
    }

    previousSearch.current = setTimeout(() => {
      setSearch(query);
      setIsEmptyResult(null);

      const filteredSites = applyFilters(allSites, query);
      updateSummary(filteredSites);
    }, 300);
  }

  function handleFarmSearch(event) {
    let trimmedstr = event.target.value;
    const query = trimmedstr.replace(/\s+$/, '');

    if (previousFarmSearch.current !== null) {
      clearTimeout(previousFarmSearch.current);
    }

    previousFarmSearch.current = setTimeout(() => {
      setFarmSearch(query);
      setIsEmptyResult(null);

      if (!query || query.length === 0) {
        // No search query, show all farms
        setFarmsData(allFarmsData);
      } else {
        // Filter farms by name (case-insensitive, contains)
        const filteredFarms = allFarmsData && allFarmsData.filter(farm =>
          farm.name?.toLowerCase().includes(query.toLowerCase())
        );
        setFarmsData(filteredFarms);
      }
    }, 300);
  }

  function handlePlanFilterChange(plan) {
    setPlanFilters(prev => {
      const newFilters = { ...prev, [plan]: !prev[plan] };
      return newFilters;
    });
  }

  // Re-apply filters whenever filter states change
  useEffect(() => {
    if (allSites) {
      const filteredSites = applyFilters(allSites);
      updateSummary(filteredSites);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planFilters, activeFilter]);

  function reportEmptyResult() {
    const tableCount = document.getElementsByTagName('table').length;
    setIsEmptyResult(tableCount === 0);
  }

  return (
    <React.Fragment>
      <div>
        {/* Grid layout with filters and action buttons */}
        <div className="subscription-controls-grid">
          <div className="filters-cell">
            {/* Filter section */}
            <div className="subscription-filters mt-3">
              <div className="filter-group mb-2">
                <strong className="filter-label">Search by site:</strong>
                <div className="flex-grow-1">
                  <SearchFilter
                    searchPlaceholder="Enter IDs or names"
                    handleSearch={handleSiteSearch}
                    noPadding={true}
                    fullWidth={true}
                  />
                </div>
              </div>
              <div className="filter-group mb-2">
                <strong className="filter-label">Search by farm:</strong>
                <div className="flex-grow-1">
                  <SearchFilter
                    searchPlaceholder="Enter farm names"
                    handleSearch={handleFarmSearch}
                    noPadding={true}
                    fullWidth={true}
                  />
                </div>
              </div>
              <div className="filter-group mb-2">
                <strong className="filter-label">Plans:</strong>
                <Form.Check
                  inline
                  type="checkbox"
                  id="filter-gold"
                  label="Gold"
                  checked={planFilters.Gold}
                  onChange={() => handlePlanFilterChange('Gold')}
                />
                <Form.Check
                  inline
                  type="checkbox"
                  id="filter-silver"
                  label="Silver"
                  checked={planFilters.Silver}
                  onChange={() => handlePlanFilterChange('Silver')}
                />
                <Form.Check
                  inline
                  type="checkbox"
                  id="filter-none"
                  label="None"
                  checked={planFilters.None}
                  onChange={() => handlePlanFilterChange('None')}
                />
              </div>
              <div className="filter-group">
                <strong className="filter-label">Active:</strong>
                <Form.Check
                  inline
                  type="radio"
                  id="filter-active-all"
                  name="activeFilter"
                  label="Both"
                  checked={activeFilter === 'all'}
                  onChange={() => setActiveFilter('all')}
                />
                <Form.Check
                  inline
                  type="radio"
                  id="filter-active-active"
                  name="activeFilter"
                  label="Active Only"
                  checked={activeFilter === 'active'}
                  onChange={() => setActiveFilter('active')}
                />
                <Form.Check
                  inline
                  type="radio"
                  id="filter-active-inactive"
                  name="activeFilter"
                  label="Inactive Only"
                  checked={activeFilter === 'inactive'}
                  onChange={() => setActiveFilter('inactive')}
                />
              </div>
            </div>
          </div>

          <div className="actions-cell">
            {!isEditMode ? (
              <Button
                variant="outline-primary"
                onClick={handleUpdateSubscriptionsClick}
                className="update-subscriptions-btn"
              >
                Update Subscriptions
              </Button>
            ) : (
              <div className="d-flex gap-2">
                <Button
                  variant="outline-danger"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSaveSubscriptions}
                  disabled={activeNewSubscription.length === 0 || isSaving}
                >
                  {isSaving ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Saving...
                    </>
                  ) : (
                    `Save Subscriptions (${activeNewSubscription.length})`
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="subscription-history-container">
          {allSites && <div className="search-summary">Summary : {GoldSites} Gold {GoldSites === 1 ? "SiteID" : "SiteIDs"}, {silverSites} Silver {silverSites === 1 ? "SiteID" : "SiteIDs"} , {notSubscribedSites} not subscribed</div>}
          {search && isEmptyResult && <StandardEmpty
            mainText="No sites found"
            subText={`No sites or site IDs matched the search text '${search}'.`} />}
          {renderCollection(farmsData, (farm, i) => <FarmSubscriptions
            key={farm.objectId}
            name={farm.name}
            farmId={farm.objectId}
            search={search}
            planFilters={planFilters}
            activeFilter={activeFilter}
            isEditMode={isEditMode}
            originalPlans={originalPlans}
            openDoneModal={() => setDoneModalShow(true)}
            onLastFarmIsRendered={(i === (farmsData.length - 1)) && reportEmptyResult}
            parent={parent}
            activeNewSubscription={activeNewSubscription}
            setActiveNewSubscriptions={setActiveNewSubscriptions}
            preloadedSites={sitesByFarm?.[farm.objectId] || null}
          />, 'No farms for this user')}
        </div>
        <DoneModal
          buttonLabel="Ok"
          show={doneModalShow}
          onClose={() => setDoneModalShow(false)}
          data={{ title: 'Contact your Admin', message: `Please contact '${parent && parent.username}' to add or upgrade this site subscription` }} />
        <DoneModal
          buttonLabel="Ok"
          show={doneSubscrptionModal}
          onClose={() => setDoneSubscrptionModal(false)}
          data={{ title: 'Subscription Activation In Progress', message: `Changes in the subscribed plan for the sites will be activated tomorrow` }} />
      </div>
    </React.Fragment>
  );
}

export default SubscriptionHistory;